import React, { useState, useMemo, useEffect } from "react";
import {
  Home,
  AlertCircle,
  TrendingDown,
  Calculator,
  PiggyBank,
  ChevronRight,
  Building2,
  Check,
  FileText,
  X,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";


export default function SimuladorHipotecaAndorra() {
  // Control de flux
  const [currentStep, setCurrentStep] = useState(0); // 0: Landing, 1: Formulari, 2: Resultats
  const [showInformeModal, setShowInformeModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactOrigin, setContactOrigin] = useState(""); // NOU STATE

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  // Dades del formulari buides inicialment
  const [importPrestec, setImportPrestec] = useState(0);
  const [terminiAnys, setTerminiAnys] = useState(30);
  const [euribor, setEuribor] = useState(2.481); // Euríbor a 12 mesos octubre 2025
  const [ingressosMensuals, setIngressosMensuals] = useState(0);

  // Càlculs derivats
  const superaLimit = importPrestec > 600000;
  const terminiMesos = terminiAnys * 12;
  const mesos7Anys = 84; // 7 anys en mesos
  const terminiMaxim = 50;
  const terminiValid = terminiAnys >= 20 && terminiAnys <= terminiMaxim;

  // Quota mensual basada en ingressos (33%)
  const quotaBeneficiari = ingressosMensuals * 0.33;

  // Càlcul de les quotes i amortització
  const simulacio = useMemo(() => {
    if (
      importPrestec <= 0 ||
      terminiAnys < 20 ||
      terminiAnys > 50 ||
      ingressosMensuals <= 0
    )
      return null;

    const capitalInicial = importPrestec;
    const quotaBeneficiariMensual = ingressosMensuals * 0.33; // 33% dels ingressos
    const taula = [];
    let capitalPendent = capitalInicial;

    // Taxes mensuals
    const taxaMensual7Anys = euribor / 100 / 12; // Euríbor + 0% (primers 7 anys)
    const taxaMensualPost7 = (euribor + 0.5) / 100 / 12; // Euríbor + 0.5% (després)

    let interessosTotals = 0;
    let interessosGovern = 0;
    let interessosPersonals = 0;

    // PRIMERS 7 ANYS (84 mesos): Quota fixa a capital, Govern paga interessos
    for (let mes = 1; mes <= Math.min(mesos7Anys, terminiMesos); mes++) {
      const interessosMes = capitalPendent * taxaMensual7Anys;
      const amortitzacioCapital = quotaBeneficiariMensual; // 100% de la quota va a capital

      capitalPendent = Math.max(0, capitalPendent - amortitzacioCapital);

      interessosTotals += interessosMes;
      interessosGovern += interessosMes;

      taula.push({
        mes,
        any: Math.ceil(mes / 12),
        quota: quotaBeneficiariMensual, // El que paga el beneficiari
        capital: amortitzacioCapital,
        interessos: interessosMes, // El que paga el Govern
        capitalPendent: capitalPendent,
        pagaGovern: true,
      });
    }

    // DEL ANY 8 EN ENDAVANT: Mètode francès amb Euríbor + 0.5%
    if (terminiMesos > mesos7Anys && capitalPendent > 0) {
      const mesosRestants = terminiMesos - mesos7Anys;

      // Quota mensual mètode francès
      const quotaFrancesa =
        (capitalPendent *
          (taxaMensualPost7 * Math.pow(1 + taxaMensualPost7, mesosRestants))) /
        (Math.pow(1 + taxaMensualPost7, mesosRestants) - 1);

      for (let mes = mesos7Anys + 1; mes <= terminiMesos; mes++) {
        const interessosMes = capitalPendent * taxaMensualPost7;
        const amortitzacioCapital = quotaFrancesa - interessosMes;

        capitalPendent = Math.max(0, capitalPendent - amortitzacioCapital);

        interessosTotals += interessosMes;
        interessosPersonals += interessosMes;

        taula.push({
          mes,
          any: Math.ceil(mes / 12),
          quota: quotaFrancesa,
          capital: amortitzacioCapital,
          interessos: interessosMes,
          capitalPendent: capitalPendent,
          pagaGovern: false,
        });
      }
    }

    // Quota promig dels primers 7 anys
    const quotaMensual7Anys = quotaBeneficiariMensual;

    // Quota després dels 7 anys (si n'hi ha)
    const quotaPost7Anys =
      taula.find((row) => row.mes > mesos7Anys)?.quota || 0;

    return {
      taula,
      quotaMensual7Anys,
      quotaPost7Anys,
      interessosTotals,
      interessosGovern,
      interessosPersonals,
      importTotal: capitalInicial + interessosPersonals, // El que realment pagarà la persona
      estalviGovern: interessosGovern,
      capitalAmortitzat7Anys: Math.min(
        quotaBeneficiariMensual * mesos7Anys,
        capitalInicial
      ),
      percentatgeAmortitzat7Anys: Math.min(
        ((quotaBeneficiariMensual * mesos7Anys) / capitalInicial) * 100,
        100
      ),
    };
  }, [
    importPrestec,
    terminiAnys,
    euribor,
    mesos7Anys,
    terminiMesos,
    ingressosMensuals,
  ]);

  // Dades per al gràfic - CORREGIT per evitar anys duplicats
  const graficData = useMemo(() => {
    if (!simulacio) return [];

    // Agafem només l'últim mes de cada any (mes 12, 24, 36...)
    return simulacio.taula
      .filter((row) => row.mes % 12 === 0)
      .map((row) => ({
        any: row.any,
        "Capital pendent": row.capitalPendent,
        "Interessos Govern": row.pagaGovern ? row.interessos : 0,
        "Interessos teus": !row.pagaGovern ? row.interessos : 0,
      }));
  }, [simulacio]);

  // Component Footer
  const Footer = () => (
    <footer
      className="bg-white border-t-2 mt-12"
      style={{ borderColor: "#97312b" }}
    >
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          {/* Logo i nom */}
          <div className="text-center md:text-left">
            <a
              href="https://fercamp.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block hover:opacity-80 transition-opacity"
            >
              <div className="flex items-center justify-center md:justify-start mb-2">
                <img
                  src="/logo.png"
                  alt="Fercamp"
                  className="h-12 object-contain"
                />
              </div>
            </a>
            <p className="text-sm text-gray-600 mb-1">Immobiliària a Andorra</p>
            <a
              href="https://fercamp.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold underline hover:no-underline transition-all"
              style={{ color: "#97312b" }}
            >
              www.fercamp.com →
            </a>
          </div>

          {/* Info de contacte */}
          <div className="text-center text-sm">
            <a
              href="mailto:fercamp@andorra.ad"
              className="mb-2 inline-flex items-center justify-center gap-2 font-medium hover:underline transition-all"
              style={{ color: "#97312b" }}
            >
              <span>📧</span>
              <span>fercamp@andorra.ad</span>
            </a>
            <br />
            <a
              href="tel:+376738721"
              className="mb-2 inline-flex items-center justify-center gap-2 font-medium hover:underline transition-all"
              style={{ color: "#97312b" }}
            >
              <span>📞</span>
              <span>+376 738 721</span>
            </a>
            <br />
            <a
              href="https://www.google.com/maps/place/IMMOBILIÀRIA+FERCAMP/@42.5698467,1.490944,3367m/data=!3m1!1e3!4m6!3m5!1s0x12af5fa844d816b1:0x76891a02bcb0ed51!8m2!3d42.5724897!4d1.48392!16s%2Fg%2F1pty39dn3?entry=ttu&g_ep=EgoyMDI1MTAyNy4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 font-medium hover:underline transition-all"
              style={{ color: "#97312b" }}
            >
              <span>📍</span>
              <span>Veure ubicació al mapa</span>
            </a>
          </div>

          {/* Legal */}
<div className="text-center md:text-right text-xs text-gray-500">
  {/* AFEGEIX AQUESTS ENLLAÇOS */}
  <div className="mb-3 space-x-3">
    <a 
      href="/avis-legal" 
      className="hover:underline"
      style={{ color: '#97312b' }}
    >
      Avís Legal
    </a>
    <span>·</span>
    <a 
      href="/politica-privacitat" 
      className="hover:underline"
      style={{ color: '#97312b' }}
    >
      Política de Privacitat
    </a>
  </div>
  
  <p className="mb-2">© 2025 Fercamp. Tots els drets reservats.</p>
  <p className="mb-1">Simulador basat en el Decret 381/2025</p>
  <p className="mb-2">del Govern d'Andorra</p>
  <p className="text-[10px] text-gray-400 mt-2">
    Eina orientativa sense garanties. Fercamp no es fa responsable<br/>
    de decisions financeres basades en aquesta simulació.
  </p>
</div>
        </div>
      </div>
    </footer>
  );

  // Càlcul dels totals per les taules
  const totals7Anys = useMemo(() => {
    if (!simulacio) return { interessos: 0, capital: 0 };
    const taula7anys = simulacio.taula.filter((row) => row.any <= 7);
    return {
      interessos: taula7anys.reduce((sum, row) => sum + row.interessos, 0),
      capital: taula7anys.reduce((sum, row) => sum + row.capital, 0),
    };
  }, [simulacio]);

  const totalsRestants = useMemo(() => {
    if (!simulacio) return { interessos: 0, capital: 0 };
    const taulaRestant = simulacio.taula.filter((row) => row.any > 7);
    return {
      interessos: taulaRestant.reduce((sum, row) => sum + row.interessos, 0),
      capital: taulaRestant.reduce((sum, row) => sum + row.capital, 0),
    };
  }, [simulacio]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Landing Page amb formulari integrat */}
      {currentStep === 0 && (
        <div className="min-h-screen p-4 py-12">
          <div className="max-w-6xl mx-auto">
            {/* Logo/Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center mb-3">
                <img
                  src="/logo.png"
                  alt="Fercamp"
                  className="h-20 object-contain"
                />
              </div>
              <p className="text-xl text-gray-700">Immobiliària Andorra</p>
            </div>

            {/* Hero Section */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-8">
              {/* Header destacat */}
              <div className="px-8 pt-8 pb-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700">
                <div className="text-center text-white">
                  <div className="inline-block bg-white/20 backdrop-blur px-4 py-2 rounded-full text-sm font-semibold mb-4">
                    ✨ Exclusiu: Nou programa 2025
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    Primera immobiliària amb simulador del{" "}
                    <span className="text-amber-300">
                      nou programa d'habitatge
                    </span>{" "}
                    del Govern
                  </h2>
                  <p className="text-lg text-white/90 max-w-3xl mx-auto">
                    El Govern d'Andorra llança un programa per ajudar-te a
                    comprar la teva primera llar:{" "}
                    <strong>paga TOTS els interessos durant 7 anys </strong>i
                    avala el 100% del préstec. No necessites entrada!
                  </p>
                </div>
              </div>

              {/* Context breu del programa */}
              <div className="px-8 py-6 bg-white border-b-2 border-gray-100">
                <div className="max-w-4xl mx-auto text-center">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    <strong className="text-blue-600">
                      Què és aquest programa?
                    </strong>
                    <br />
                    Una iniciativa del Govern per facilitar l'accés al{" "}
                    <strong>primer habitatge de propietat </strong>a persones
                    amb un mínim de 7 anys de residència a Andorra. L'ajuda
                    consisteix en: durant els primers <strong>7 anys</strong>,
                    el Govern assumeix <strong>tots els interessos </strong>
                    de la hipoteca (a fons perdut). Tu només pagues capital, que
                    és el <strong>33% dels teus ingressos</strong> mensuals.
                  </p>
                </div>
              </div>

              {/* Grid: Els 3 pilars + Formulari */}
              <div className="grid lg:grid-cols-2 gap-8 p-8">
                {/* Columna esquerra: Com funciona */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Com funciona el programa?
                  </h3>

                  {/* Explicació general */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border-2 border-blue-200">
                    <p className="text-sm text-gray-800 leading-relaxed">
                      El programa està dividit en <strong>dues fases</strong>:
                      els primers 7 anys (amb ajuda del Govern) i del any 8 en
                      endavant (hipoteca convencional). Així és com funciona
                      cada fase:
                    </p>
                  </div>

                  {/* FASE 1: Primers 7 anys */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-300 shadow-md">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="bg-green-500 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                        <span className="text-white font-bold text-lg">
                          1-7
                        </span>
                      </div>
                      <div>
                        <h4 className="font-bold text-xl mb-1 text-gray-900">
                          Primers 7 anys
                        </h4>
                        <p className="text-xs text-green-700 font-semibold">
                          Fase amb ajuda del Govern
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-white/60 rounded-lg p-3">
                        <p className="text-sm text-gray-800 mb-2">
                          <strong className="text-green-700">
                            💰 El que tu pagues:
                          </strong>
                        </p>
                        <p className="text-sm text-gray-700">
                          Destines el{" "}
                          <strong>33% dels teus ingressos mensuals</strong> nets
                          al pagament del capital. El 100% d'aquesta quota va a
                          amortitzar el préstec.
                        </p>
                      </div>

                      <div className="bg-white/60 rounded-lg p-3">
                        <p className="text-sm text-gray-800 mb-2">
                          <strong className="text-green-700">
                            🏛️ El que paga el Govern:
                          </strong>
                        </p>
                        <p className="text-sm text-gray-700">
                          Tots els <strong>interessos del préstec</strong>{" "}
                          (Euríbor + 0%) a fons perdut. Tu no pagues res
                          d'interessos durant aquest període.
                        </p>
                      </div>

                      <div className="bg-green-100 rounded-lg p-3 border border-green-300">
                        <p className="text-xs text-green-800 font-semibold">
                          ✨ Resultat: Amortitzes el deute molt més ràpid que en
                          una hipoteca normal perquè el 100% del que pagues va a
                          capital!
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* FASE 2: Del any 8 en endavant */}
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl border-2 border-orange-300 shadow-md">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="bg-orange-500 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                        <span className="text-white font-bold text-lg">8+</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-xl mb-1 text-gray-900">
                          Del any 8 fins al final
                        </h4>
                        <p className="text-xs text-orange-700 font-semibold">
                          Hipoteca convencional
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-white/60 rounded-lg p-3">
                        <p className="text-sm text-gray-800 mb-2">
                          <strong className="text-orange-700">
                            💳 El que tu pagues:
                          </strong>
                        </p>
                        <p className="text-sm text-gray-700">
                          <strong>Capital + Interessos</strong> amb sistema
                          d'amortització francès. Tipus d'interès:{" "}
                          <strong>Euríbor + 0,5%</strong>.
                        </p>
                      </div>

                      <div className="bg-orange-100 rounded-lg p-3 border border-orange-300">
                        <p className="text-xs text-orange-800 font-semibold">
                          ⚠️ Important: La quota mensual normalment serà més
                          alta que durant els primers 7 anys, ja que hauràs de
                          pagar també els interessos.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Punts clau del programa */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-5">
                    <h4 className="font-bold text-lg text-gray-900 mb-3">
                      ✓ Avantatges clau del programa:
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-600 font-bold">✓</span>
                        <span>
                          <strong>No necessites entrada inicial</strong> – El
                          Govern avala el 100% durant 7 anys
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-600 font-bold">✓</span>
                        <span>
                          <strong>El banc té garantia total</strong> – Accés
                          facilitat al finançament
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-600 font-bold">✓</span>
                        <span>
                          <strong>Import màxim: 600.000€</strong> – Preu total
                          de l'habitatge
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-600 font-bold">✓</span>
                        <span>
                          <strong>Termini flexible</strong> – Entre 20 i 50 anys
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-600 font-bold">✓</span>
                        <span>
                          <strong>Requisit</strong> – Mínim 7 anys de residència
                          legal i permanent a Andorra
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Exemple pràctic */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5 rounded-xl text-white shadow-lg">
                    <p className="text-sm font-semibold mb-2">
                      📊 Exemple pràctic del Govern:
                    </p>
                    <p className="text-sm leading-relaxed">
                      Per un préstec de <strong>400.000€</strong> a 30 anys amb
                      una quota de <strong>1.150€/mes</strong>, el Govern pagarà{" "}
                      <strong>61.252€ en interessos</strong> durant 7 anys. Tu
                      hauràs amortitzat <strong>96.600€ de capital</strong> (24%
                      del total).
                      <span className="block mt-2 text-amber-300 font-semibold">
                        Això en una hipoteca normal seria impossible!
                      </span>
                    </p>
                  </div>

                  {/* Nota advertència */}
                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4">
                    <p className="text-xs text-yellow-900">
                      <strong>⚠️ Recorda:</strong> Durant els primers 7 anys no
                      pots vendre ni llogar l'habitatge. A partir del any 8, si
                      vens abans dels 20 anys, hauràs de retornar al Govern 3
                      vegades el que han aportat. Consulta amb un assessor
                      financer abans de decidir.
                    </p>
                  </div>
                </div>

                {/* Columna dreta: Formulari */}
                <div>
                  <div
                    className="bg-gradient-to-br from-stone-50 to-amber-50 rounded-xl p-6 border-2 sticky top-4"
                    style={{ borderColor: "#97312b" }}
                  >
                    <h3 className="text-2xl font-bold mb-2 text-gray-900 text-center">
                      Calcula la teva hipoteca
                    </h3>
                    <p className="text-sm text-gray-600 text-center mb-6">
                      Descobreix quant pots estalviar amb aquest programa
                    </p>

                    <div className="space-y-5">
                      {/* Ingressos mensuals */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Ingressos mensuals nets (€)
                        </label>
                        <input
                          type="number"
                          value={ingressosMensuals || ""}
                          onChange={(e) =>
                            setIngressosMensuals(Number(e.target.value))
                          }
                          placeholder="Ex: 3.500"
                          className="w-full px-4 py-3 border-2 border-stone-300 rounded-lg focus:ring-2 focus:border-transparent text-lg"
                          min="0"
                          step="100"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          💡 Els teus ingressos mensuals nets (o els ingressos
                          de la unitat familiar de convivència). La quota serà
                          el 33% d'aquest import (aprox.{" "}
                          {(ingressosMensuals * 0.33).toLocaleString("ca-ES", {
                            maximumFractionDigits: 0,
                          })}
                          €/mes).
                        </p>
                      </div>

                      {/* Import préstec */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Import del préstec que necessites (€)
                        </label>
                        <input
                          type="number"
                          value={importPrestec || ""}
                          onChange={(e) =>
                            setImportPrestec(Number(e.target.value))
                          }
                          placeholder="Ex: 400.000"
                          className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:border-transparent text-lg focus:ring-blue-500"
                          min="0"
                          step="1000"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          💡 La quantitat que necessites finançar. El màxim del
                          programa és 600.000€.
                        </p>
                      </div>

                      {/* Validació límit */}
                      {importPrestec > 600000 && (
                        <div className="p-4 rounded-lg border-2 bg-red-50 border-red-300">
                          <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            L'import supera el límit del programa (600.000€)
                          </p>
                        </div>
                      )}

                      {/* Termini */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Termini del préstec (anys)
                        </label>
                        <input
                          type="number"
                          value={terminiAnys === 0 ? "" : terminiAnys}
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? 0
                                : Number(e.target.value);
                            setTerminiAnys(value);
                          }}
                          className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:border-transparent text-lg focus:ring-blue-500"
                          min="20"
                          max={terminiMaxim}
                          placeholder="Ex: 30"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          💡 Durada del préstec en anys (entre 20 i 50). L'edat
                          màxima al finalitzar és de 80 anys.
                        </p>
                      </div>

                      {/* Euríbor */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Euríbor a 12 mesos (%)
                        </label>
                        <input
                          type="number"
                          value={euribor}
                          onChange={(e) => setEuribor(Number(e.target.value))}
                          className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:border-transparent text-lg focus:ring-blue-500"
                          min="0"
                          max="10"
                          step="0.01"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          💡 Tipus d'interès de referència (octubre 2025:
                          2,481%). Anys 1-7: Euríbor + 0% (paga el Govern) | Del
                          any 8: Euríbor + 0,5%.
                        </p>
                      </div>
                    </div>

                    {/* Botó */}
                    <div className="mt-6">
                      <button
                        onClick={() => setCurrentStep(2)}
                        disabled={
                          !terminiValid ||
                          superaLimit ||
                          importPrestec === 0 ||
                          ingressosMensuals === 0
                        }
                        className="w-full px-6 py-4 text-white rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                        style={{
                          background:
                            !terminiValid ||
                            superaLimit ||
                            importPrestec === 0 ||
                            ingressosMensuals === 0
                              ? "#9ca3af"
                              : "linear-gradient(135deg, #97312b 0%, #c94235 100%)",
                        }}
                      >
                        <Calculator className="w-5 h-5" />
                        Veure la meva simulació
                      </button>
                    </div>

                    {/* Advertències */}
                    {(superaLimit ||
                      !terminiValid ||
                      importPrestec === 0 ||
                      ingressosMensuals === 0) && (
                      <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                        <p className="text-sm font-medium text-yellow-800 flex items-center gap-2 mb-2">
                          <AlertCircle className="w-4 h-4" />
                          {importPrestec === 0 || ingressosMensuals === 0
                            ? "Completa el formulari:"
                            : "Comprova aquestes dades:"}
                        </p>
                        <ul className="text-sm text-yellow-700 space-y-1">
                          {ingressosMensuals === 0 && (
                            <li>• Introdueix els teus ingressos mensuals</li>
                          )}
                          {importPrestec === 0 && (
                            <li>
                              • Introdueix l'import del préstec que necessites
                            </li>
                          )}
                          {superaLimit && importPrestec > 0 && (
                            <li>
                              • L'import supera el límit del programa (600.000€)
                            </li>
                          )}
                          {!terminiValid && (
                            <li>• El termini ha de ser entre 20 i 50 anys</li>
                          )}
                        </ul>
                      </div>
                    )}

                    <p className="text-xs text-center text-gray-500 mt-4">
                      Simulació gratuïta i instantània · Sense registre ni
                      compromís
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer info */}
            <div className="text-center text-sm text-gray-600 bg-white rounded-xl shadow-lg p-5 border-2 border-gray-200 mb-8">
              <p className="font-semibold text-gray-800 mb-2">
                ⚖️ Avís legal i limitació de responsabilitat
              </p>
              <p className="text-xs text-gray-700 mb-2">
                Aquest simulador és una <strong>eina orientativa</strong> basada
                en el Decret 381/2025 del Govern d'Andorra. Els càlculs són
                aproximats i poden variar segons les condicions específiques de
                cada entitat bancària.
              </p>
              <p className="text-xs text-gray-700 mb-2">
                <strong>Fercamp no garanteix l'exactitud</strong> dels resultats
                ni es fa responsable de les decisions financeres preses en base
                a aquesta simulació.
              </p>
              <p className="text-xs text-gray-800 font-medium">
                📋 És imprescindible consultar amb la teva entitat bancària i
                l'Institut Nacional de l'Habitatge per obtenir les condicions
                exactes, avaluar la teva solvència i conèixer l'impacte real en
                la teva situació financera personal.
              </p>
            </div>

            {/* CTA Secundari */}
            <div
              className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-2"
              style={{ borderColor: "#97312b" }}
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: "#97312b" }}
                  >
                    <Building2 className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Busques habitatge a Andorra?
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Tenim habitatges que compleixen els requisits del programa.
                    Explica'ns què necessites i t'enviem opcions personalitzades
                    al teu pressupost.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start text-sm text-gray-600">
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full">
                      ✓ Fins a 600.000€
                    </span>
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                      ✓ Residència permanent (per entrar-hi a viure)
                    </span>
                    <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                      ✓ Aptes pel programa
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <button
                    onClick={() => {
                      setContactOrigin("landing");
                      setShowContactModal(true);
                    }}
                    className="text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all inline-flex items-center gap-2"
                    style={{
                      background:
                        "linear-gradient(135deg, #97312b 0%, #c94235 100%)",
                    }}
                  >
                    Veure habitatges
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      )}

      {/* Resultats */}
      {currentStep === 2 && simulacio && (
        <div className="max-w-7xl mx-auto p-4 md:p-8 py-12">
          {/* Header amb botó tornar */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => setCurrentStep(0)}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2 font-medium"
            >
              ← Modificar dades
            </button>
            <div className="flex items-center">
              <img
                src="/logo.png"
                alt="Fercamp"
                className="h-8 object-contain"
              />
            </div>
          </div>

          {/* Resultats */}
          <div className="space-y-6">
            {/* Resum destacat amb estalvi */}
            <div className="rounded-2xl shadow-2xl p-8 text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700">
              <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                <PiggyBank className="w-8 h-8" />
                El teu estalvi amb el programa del Govern
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/10 rounded-xl p-6 backdrop-blur">
                  <p className="text-sm opacity-90 mb-2">
                    Interessos que paga el Govern
                  </p>
                  <p className="text-4xl font-bold mb-1">
                    {simulacio.interessosGovern.toLocaleString("ca-ES", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    €
                  </p>
                  <p className="text-xs opacity-75">
                    Durant els primers 7 anys
                  </p>
                </div>
                <div className="bg-white/10 rounded-xl p-6 backdrop-blur">
                  <p className="text-sm opacity-90 mb-2">
                    Interessos que TU pagaràs
                  </p>
                  <p className="text-4xl font-bold mb-1">
                    {simulacio.interessosPersonals.toLocaleString("ca-ES", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    €
                  </p>
                  <p className="text-xs opacity-75">Del any 8 fins al final</p>
                </div>
              </div>
            </div>

            {/* Taula resum AMPLIADA */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Resum del préstec
                </h3>
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold">
                  ✓ {((totals7Anys.capital / importPrestec) * 100).toFixed(1)}%
                  amortitzat en 7 anys
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="border-l-4 border-blue-600 pl-4">
                  <p className="text-sm text-gray-600 mb-1">Capital inicial</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {importPrestec.toLocaleString("ca-ES")} €
                  </p>
                </div>
                <div className="border-l-4 border-indigo-600 pl-4">
                  <p className="text-sm text-gray-600 mb-1">Termini</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {terminiAnys} anys
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ({terminiMesos} mesos)
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <p className="text-sm text-gray-600 mb-1">
                    Quota mensual (7 anys)
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {simulacio.quotaMensual7Anys.toLocaleString("ca-ES", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    €
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <p className="text-sm text-gray-600 mb-1">
                    💰 Interessos paga Govern (Estalvi)
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {simulacio.interessosGovern.toLocaleString("ca-ES", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    €
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Anys 1-7</p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <p className="text-sm text-gray-600 mb-1">
                    💳 Interessos que tu pagues
                  </p>
                  <p className="text-2xl font-bold text-orange-600">
                    {simulacio.interessosPersonals.toLocaleString("ca-ES", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    €
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Del any 8 en endavant
                  </p>
                </div>
                <div className="border-l-4 border-gray-400 pl-4">
                  <p className="text-sm text-gray-600 mb-1">
                    📊 Interessos totals
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {simulacio.interessosTotals.toLocaleString("ca-ES", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    €
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Govern + Tu</p>
                </div>
                <div className="border-l-4 border-blue-400 pl-4 md:col-span-2">
                  <p className="text-sm text-gray-600 mb-1">
                    💵 Total que pagaràs (només tu)
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {simulacio.importTotal.toLocaleString("ca-ES", {
                      maximumFractionDigits: 0,
                    })}{" "}
                    €
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Capital + Interessos que tu pagues
                  </p>
                </div>
              </div>
            </div>

            {/* CTA per avisar quan entri en vigor */}
            <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-8 text-center text-white">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur rounded-full mb-4">
                  <span className="text-4xl">🔔</span>
                </div>
                <h3 className="text-3xl font-bold mb-3">
                  Vols que t'avisem quan s'obri la convocatòria?
                </h3>
                <p className="text-lg mb-6 text-white/90 max-w-2xl mx-auto leading-relaxed">
                  El programa encara no ha obert convocatòria oficial. Deixa'ns
                  les teves dades i
                  <strong> seràs dels primers en saber-ho</strong> quan s'obri.
                  A més, t'ajudarem a buscar habitatges que encaixin amb els
                  requisits.
                </p>

                <div className="grid md:grid-cols-3 gap-3 mb-6 max-w-2xl mx-auto text-sm">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                    <p className="font-semibold">📧 Avís prioritari</p>
                    <p className="text-xs text-white/80">
                      T'informem al moment
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                    <p className="font-semibold">🏠 Catàleg exclusiu</p>
                    <p className="text-xs text-white/80">Fins a 600.000€</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                    <p className="font-semibold">✓ Sense compromís</p>
                    <p className="text-xs text-white/80">Servei gratuït</p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setContactOrigin("avis");
                    setShowContactModal(true);
                  }}
                  className="bg-white text-indigo-700 px-8 py-4 rounded-xl text-lg font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all inline-flex items-center gap-3"
                >
                  <span>✓</span>
                  Sí, vull que m'aviseu
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Gràfic */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold mb-3 text-gray-900 flex items-center gap-2">
                <TrendingDown
                  className="w-6 h-6"
                  style={{ color: "#97312b" }}
                />
                Evolució del capital pendent
              </h3>
              <p
                className="text-sm text-gray-600 mb-6 border-l-4 p-3 rounded"
                style={{ backgroundColor: "#fef3e8", borderColor: "#97312b" }}
              >
                📈 <strong>Què mostra aquest gràfic?</strong> Veuràs com el
                deute (capital pendent) disminueix any a any. Durant els primers
                7 anys, el Govern paga els interessos, així que el 100% del que
                tu pagues va directament a amortitzar capital. En una hipoteca
                normal, gran part de la quota inicial va a pagar interessos,
                però amb aquest programa tot va a reduir el deute!
              </p>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={graficData}>
                  <defs>
                    <linearGradient
                      id="colorCapital"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#97312b" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#97312b"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="any"
                    label={{
                      value: "Any",
                      position: "insideBottom",
                      offset: -5,
                    }}
                  />
                  <YAxis
                    label={{
                      value: "Euros (€)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip
                    formatter={(value) =>
                      value.toLocaleString("ca-ES", {
                        maximumFractionDigits: 0,
                      }) + " €"
                    }
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="Capital pendent"
                    stroke="#97312b"
                    fillOpacity={1}
                    fill="url(#colorCapital)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Comparativa destacada */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <PiggyBank className="w-6 h-6 text-green-600" />
                Avantatges i consideracions del programa
              </h3>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border-2 border-red-200">
                    <p className="text-sm font-semibold text-red-700 mb-2">
                      ❌ Hipoteca tradicional
                    </p>
                    <p className="text-xs text-gray-600">
                      Els primers anys, gairebé tot el que pagues són
                      interessos. El capital baixa molt lentament.
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-green-300">
                    <p className="text-sm font-semibold text-green-700 mb-2">
                      ✅ Primers 7 anys (amb programa)
                    </p>
                    <p className="text-xs text-gray-600">
                      El 100% del que pagues va a reduir el deute. Amortitzes un{" "}
                      {((totals7Anys.capital / importPrestec) * 100).toFixed(1)}
                      % del capital!
                    </p>
                  </div>
                </div>
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                  <p className="text-sm font-semibold text-amber-800 mb-2">
                    ⚠️ Del any 8 en endavant
                  </p>
                  <p className="text-xs text-gray-700 mb-2">
                    La quota es calcularà amb sistema francès (Euríbor + 0,5%) i{" "}
                    <strong>normalment serà més alta</strong> que durant els
                    primers 7 anys, ja que hauràs de pagar tant capital com
                    interessos en menys temps.
                  </p>
                  <p className="text-xs text-gray-700 mb-2">
                    <strong>Estimació amb Euríbor constant:</strong> Si
                    l'Euríbor es mantingués al {euribor}% actual (poc probable),
                    la quota passaria d'aproximadament{" "}
                    <strong>
                      {simulacio.quotaMensual7Anys.toLocaleString("ca-ES", {
                        maximumFractionDigits: 0,
                      })}
                      €
                    </strong>{" "}
                    a{" "}
                    <strong>
                      {simulacio.quotaPost7Anys.toLocaleString("ca-ES", {
                        maximumFractionDigits: 0,
                      })}
                      €
                    </strong>{" "}
                    mensuals.
                  </p>
                  <p className="text-xs text-amber-900 font-medium">
                    📊 <strong>Incertesa:</strong> L'Euríbor pot variar
                    significativament en 7 anys. Si puja, la quota serà encara
                    més alta. Si baixa, podria ser més baixa. Aquesta simulació
                    NO pot predir el futur.
                  </p>
                </div>
              </div>
            </div>

            {/* Taules d'amortització */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">
                Taula d'amortització detallada per mesos
              </h3>

              {/* Primers 7 anys */}
              <div className="mb-8">
                <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-4">
                  <h4 className="text-xl font-semibold text-green-700 flex items-center gap-2 mb-2">
                    <Check className="w-5 h-5" />
                    Primers 7 anys (Govern paga interessos) - {mesos7Anys} mesos
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <span className="text-gray-700">
                        💰 Total interessos pagats pel Govern:
                      </span>
                      <span className="font-bold text-green-700 ml-2">
                        {totals7Anys.interessos.toLocaleString("ca-ES", {
                          maximumFractionDigits: 0,
                        })}{" "}
                        €
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-700">
                        📉 Total capital amortitzat:
                      </span>
                      <span className="font-bold text-green-700 ml-2">
                        {totals7Anys.capital.toLocaleString("ca-ES", {
                          maximumFractionDigits: 0,
                        })}{" "}
                        €
                      </span>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-green-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">
                          Mes
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          Quota mensual
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          Capital
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          Interessos (Govern)
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          Capital pendent
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {simulacio.taula
                        .filter((row) => row.any <= 7)
                        .map((row, idx) => (
                          <tr
                            key={idx}
                            className={`border-b hover:bg-gray-50 ${
                              row.mes % 12 === 0
                                ? "bg-green-100 font-semibold"
                                : ""
                            }`}
                          >
                            <td className="px-4 py-2">
                              {row.mes % 12 === 0
                                ? `Mes ${row.mes} (Any ${row.any})`
                                : `Mes ${row.mes}`}
                            </td>
                            <td className="px-4 py-2 text-right font-medium">
                              {row.quota.toLocaleString("ca-ES", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              €
                            </td>
                            <td className="px-4 py-2 text-right">
                              {row.capital.toLocaleString("ca-ES", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              €
                            </td>
                            <td className="px-4 py-2 text-right text-green-600">
                              {row.interessos.toLocaleString("ca-ES", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              €
                            </td>
                            <td className="px-4 py-2 text-right font-semibold">
                              {row.capitalPendent.toLocaleString("ca-ES", {
                                maximumFractionDigits: 0,
                              })}{" "}
                              €
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Les files destacades en verd corresponen al final de cada any
                </p>
              </div>

              {/* Resta del préstec */}
              <div>
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
                  <h4 className="text-xl font-semibold text-blue-700 mb-2">
                    Del any 8 fins al final (Tu pagues interessos) -{" "}
                    {terminiMesos - mesos7Anys} mesos
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                    <div>
                      <span className="text-gray-700">
                        💳 Total interessos que tu pagues:
                      </span>
                      <span className="font-bold text-blue-700 ml-2">
                        {totalsRestants.interessos.toLocaleString("ca-ES", {
                          maximumFractionDigits: 0,
                        })}{" "}
                        €
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-700">
                        📉 Total capital amortitzat:
                      </span>
                      <span className="font-bold text-blue-700 ml-2">
                        {totalsRestants.capital.toLocaleString("ca-ES", {
                          maximumFractionDigits: 0,
                        })}{" "}
                        €
                      </span>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-blue-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">
                          Mes
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          Quota mensual
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          Capital
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          Interessos (Tu)
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          Capital pendent
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {simulacio.taula
                        .filter((row) => row.any > 7)
                        .map((row, idx) => (
                          <tr
                            key={idx}
                            className={`border-b hover:bg-gray-50 ${
                              row.mes % 12 === 0
                                ? "bg-blue-100 font-semibold"
                                : ""
                            }`}
                          >
                            <td className="px-4 py-2">
                              {row.mes % 12 === 0
                                ? `Mes ${row.mes} (Any ${row.any})`
                                : `Mes ${row.mes}`}
                            </td>
                            <td className="px-4 py-2 text-right font-medium">
                              {row.quota.toLocaleString("ca-ES", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              €
                            </td>
                            <td className="px-4 py-2 text-right">
                              {row.capital.toLocaleString("ca-ES", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              €
                            </td>
                            <td className="px-4 py-2 text-right text-blue-600">
                              {row.interessos.toLocaleString("ca-ES", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              €
                            </td>
                            <td className="px-4 py-2 text-right font-semibold">
                              {row.capitalPendent.toLocaleString("ca-ES", {
                                maximumFractionDigits: 0,
                              })}{" "}
                              €
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Les files destacades en blau corresponen al final de cada any
                </p>
              </div>
            </div>

            {/* Info addicional */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
              <p className="text-sm text-blue-900 mb-3">
                <strong>📌 Condicions importants del programa:</strong>
              </p>
              <ul className="text-sm text-blue-900 space-y-2 list-disc list-inside">
                <li>
                  Has de destinar el <strong>33% dels teus ingressos</strong> al
                  pagament del capital durant 7 anys
                </li>
                <li>
                  <strong>No pots vendre ni llogar</strong> l'habitatge durant
                  els primers 7 anys
                </li>
                <li>
                  A partir del any 8,{" "}
                  <strong>la quota mensual normalment serà més alta</strong>{" "}
                  (sistema francès amb Euríbor + 0,5%)
                </li>
                <li>
                  Requisit: Mínim <strong>7 anys de residència</strong> legal,
                  efectiva i permanent a Andorra
                </li>
                <li>
                  Edat màxima al finalitzar: <strong>80 anys</strong>
                </li>
              </ul>
            </div>

            {/* Disclaimer legal reforçat */}
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-6">
              <p className="text-sm font-bold text-yellow-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                ⚖️ Avís legal i limitació de responsabilitat
              </p>
              <div className="text-xs text-yellow-900 space-y-2">
                <p>
                  <strong>1. Caràcter orientatiu:</strong> Aquest simulador
                  proporciona càlculs aproximats basats en el Decret 381/2025.
                  Els resultats són estimatius i poden diferir dels càlculs
                  oficials de les entitats bancàries.
                </p>
                <p>
                  <strong>2. Sense garanties:</strong> Fercamp no garanteix
                  l'exactitud, completesa o actualització dels càlculs. Les
                  condicions reals poden variar segons la teva situació
                  financera personal, la política de cada banc i futurs canvis
                  normatius.
                </p>
                <p>
                  <strong>3. No constitueix assessorament:</strong> Aquesta eina
                  no substitueix l'assessorament financer professional. No pren
                  decisions financeres basant-te únicament en aquesta simulació.
                </p>
                <p>
                  <strong>4. Exclusió de responsabilitat:</strong> Fercamp no es
                  fa responsable de les decisions financeres preses en base a
                  aquesta simulació ni de possibles discrepàncies amb les
                  condicions reals ofertes per les entitats bancàries.
                </p>
                <p className="font-semibold mt-3 pt-3 border-t border-yellow-300">
                  📞 <strong>Contacta amb:</strong> La teva entitat bancària,
                  l'Institut Nacional de l'Habitatge (INH) i un assessor
                  financer per obtenir informació personalitzada, verificar la
                  teva elegibilitat i conèixer l'impacte real en la teva
                  situació financera abans de prendre qualsevol compromís.
                </p>
              </div>
            </div>

            {/* CTA Principal Fercamp */}
            <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-2xl overflow-hidden mt-8">
              <div className="p-8 md:p-10 text-white text-center">
                <Building2 className="w-16 h-16 mx-auto mb-4 opacity-90" />
                <h3 className="text-3xl font-bold mb-3">
                  Ja saps quant pots estalviar. Ara busquem el teu habitatge!
                </h3>
                <p className="text-lg mb-6 text-white/90 max-w-2xl mx-auto">
                  A Fercamp som experts en el mercat andorrà i coneixem
                  perfectament el programa del Govern. T'ajudem a trobar
                  l'habitatge ideal dins del teu pressupost i et guiem en tot el
                  procés.
                </p>

                <div className="grid md:grid-cols-3 gap-4 mb-6 max-w-3xl mx-auto">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <p className="font-semibold mb-1">🏠 Catàleg exclusiu</p>
                    <p className="text-sm text-white/80">
                      Habitatges fins a 600.000€
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <p className="font-semibold mb-1">
                      📋 Assessorament complet
                    </p>
                    <p className="text-sm text-white/80">
                      Des de la tria del immoble fins a les claus
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <p className="font-semibold mb-1">⚡ Procés ràpid</p>
                    <p className="text-sm text-white/80">
                      Experts en el sector i el programa del Govern
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setContactOrigin("resultats");
                    setShowContactModal(true);
                  }}
                  className="bg-white text-blue-700 px-8 py-4 rounded-xl text-lg font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all inline-flex items-center gap-3"
                >
                  <Home className="w-6 h-6" />
                  Explica'ns què busques
                  <ChevronRight className="w-5 h-5" />
                </button>

                <p className="text-sm text-white/70 mt-4">Sense compromís</p>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      )}

      {/* Modal per sol·licitar informe */}
      {showInformeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Header del modal */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Sol·licita el teu informe en PDF
                  </h3>
                  <p className="text-gray-600">
                    Omple les dades i rebràs l'informe complet al teu correu
                  </p>
                </div>
                <button
                  onClick={() => setShowInformeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Resum de la simulació */}
              <div className="rounded-lg p-4 mb-6 border-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Resum de la teva simulació:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Import préstec:</span>
                    <span className="font-semibold ml-2">
                      {importPrestec.toLocaleString("ca-ES")} €
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Termini:</span>
                    <span className="font-semibold ml-2">
                      {terminiAnys} anys
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Quota mensual:</span>
                    <span className="font-semibold ml-2">
                      {simulacio?.quotaMensual7Anys.toLocaleString("ca-ES", {
                        maximumFractionDigits: 0,
                      })}{" "}
                      €
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Estalvi Govern:</span>
                    <span className="font-semibold ml-2 text-green-600">
                      {simulacio?.interessosGovern.toLocaleString("ca-ES", {
                        maximumFractionDigits: 0,
                      })}{" "}
                      €
                    </span>
                  </div>
                </div>
              </div>

              {/* Avís legal abans del formulari */}
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4">
                <p className="text-xs text-yellow-900">
                  <strong>⚠️ Nota important:</strong> En enviar aquest
                  formulari, acceptes que has llegit i entès que aquesta
                  simulació és orientativa. Fercamp no garanteix l'exactitud
                  dels càlculs ni es fa responsable de les decisions financeres
                  preses en base a aquesta informació.
                </p>
              </div>

              {/* Formulari Tally embedit */}
              <div
                className="rounded-lg overflow-hidden"
                style={{ minHeight: "600px" }}
              >
                <iframe
                  key={`tally-${importPrestec}-${showInformeModal}`}
                  src={`https://tally.so/embed/mJaY6J?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1&import_prestec=${importPrestec}&&termini_anys=${terminiAnys}&euribor=${euribor}&quota_mensual=${Math.round(
                    simulacio?.quotaMensual7Anys || 0
                  )}&interessos_govern=${Math.round(
                    simulacio?.interessosGovern || 0
                  )}&interessos_personals=${Math.round(
                    simulacio?.interessosPersonals || 0
                  )}`}
                  width="100%"
                  height="552"
                  frameBorder="0"
                  marginHeight="0"
                  marginWidth="0"
                  title="Simulador Fercamp 7%"
                  className="w-full"
                  style={{ border: 0 }}
                />
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center mb-3">
                  🔒 Les teves dades estan protegides i només s'utilitzaran per
                  enviar-te l'informe
                </p>
                <div className="text-center">
                  <button
                    onClick={() => setShowInformeModal(false)}
                    className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
                  >
                    Tancar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal per contactar amb Fercamp */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Header del modal */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="mb-4">
                    <img
                      src="/logo.png"
                      alt="Fercamp"
                      className="h-10 object-contain mb-2"
                    />
                    <h3 className="text-2xl font-bold text-gray-900">
                      {contactOrigin === "resultats" &&
                        "Troba el teu habitatge"}
                      {contactOrigin === "landing" &&
                        "Busques habitatge a Andorra?"}
                      {contactOrigin === "flotant" && "Contacta amb nosaltres"}
                      {contactOrigin === "avis" &&
                        "Avisa'm quan s'obri el programa"}
                    </h3>
                  </div>
                  <p className="text-gray-600">
                    {contactOrigin === "resultats" &&
                      "Ja saps quant pots estalviar. Ara t'ajudem a trobar l'habitatge ideal dins del teu pressupost."}
                    {contactOrigin === "landing" &&
                      "Tenim habitatges que compleixen els requisits del programa. Explica'ns què necessites."}
                    {contactOrigin === "flotant" &&
                      "Explica'ns què busques i t'enviarem opcions personalitzades al teu pressupost."}
                    {contactOrigin === "avis" &&
                      "Deixa'ns les teves dades i seràs dels primers en saber quan s'obri la convocatòria oficial. També t'ajudarem a trobar l'habitatge perfecte."}
                  </p>
                </div>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Beneficis destacats */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-2xl mb-1">🏠</p>
                  <p className="text-xs font-semibold text-gray-700">
                    Catàleg exclusiu
                  </p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-2xl mb-1">⚡</p>
                  <p className="text-xs font-semibold text-gray-700">
                    Resposta ràpida
                  </p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-2xl mb-1">✓</p>
                  <p className="text-xs font-semibold text-gray-700">
                    Sense compromís
                  </p>
                </div>
              </div>

              {/* Informació de la simulació si està disponible */}
              {simulacio &&
                currentStep === 2 &&
                contactOrigin === "resultats" && (
                  <div className="rounded-lg p-4 mb-6 border-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      📊 Dades de la teva simulació:
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Pressupost màxim:</span>
                        <span className="font-semibold ml-2">
                          {importPrestec.toLocaleString("ca-ES")} €
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Quota mensual:</span>
                        <span className="font-semibold ml-2">
                          {simulacio.quotaMensual7Anys.toLocaleString("ca-ES", {
                            maximumFractionDigits: 0,
                          })}{" "}
                          €
                        </span>
                      </div>
                    </div>
                  </div>
                )}

              {/* Avís legal abans del formulari */}
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4">
                <p className="text-xs text-yellow-900">
                  <strong>⚠️ Nota important:</strong> En enviar aquest
                  formulari, acceptes que has llegit i entès que aquesta
                  simulació és orientativa. Fercamp no garanteix l'exactitud
                  dels càlculs ni es fa responsable de les decisions financeres
                  preses en base a aquesta informació.
                </p>
              </div>

              {/* Formulari Tally embedit */}
              <div
                className="rounded-lg overflow-hidden"
                style={{ minHeight: "600px" }}
              >
                <iframe
                  key={`tally-${importPrestec}-${showInformeModal}`}
                  src={`https://tally.so/embed/mJaY6J?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1&import_prestec=${importPrestec}&&termini_anys=${terminiAnys}&euribor=${euribor}&quota_mensual=${Math.round(
                    simulacio?.quotaMensual7Anys || 0
                  )}&interessos_govern=${Math.round(
                    simulacio?.interessosGovern || 0
                  )}&interessos_personals=${Math.round(
                    simulacio?.interessosPersonals || 0
                  )}`}
                  width="100%"
                  height="552"
                  frameBorder="0"
                  marginHeight="0"
                  marginWidth="0"
                  title="Simulador Fercamp 7%"
                  className="w-full"
                  style={{ border: 0 }}
                />
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center mb-3">
                  🔒 Les teves dades estan protegides i només s'utilitzaran per
                  contactar-te
                </p>
                <div className="text-center">
                  <button
                    onClick={() => setShowContactModal(false)}
                    className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
                  >
                    Tancar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botó flotant sticky */}
      {(currentStep === 0 || currentStep === 2) && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => {
              setContactOrigin("flotant");
              setShowContactModal(true);
            }}
            className="text-white px-6 py-4 rounded-full font-bold shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all inline-flex items-center gap-2"
            style={{
              background: "linear-gradient(135deg, #97312b 0%, #c94235 100%)",
            }}
          >
            <Home className="w-5 h-5" />
            <span className="hidden sm:inline">Busques pis?</span>
            <span className="sm:hidden">Contacta</span>
          </button>
        </div>
      )}
      {(showInformeModal || showContactModal) && (
        <script src="https://tally.so/widgets/embed.js" async />
      )}
    </div>
  );
}
